import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import { db } from "../firebase";
import { collection, addDoc } from "firebase/firestore";
import emailjs from "emailjs-com";

import {
    TextField, Button, MenuItem, Checkbox, FormControlLabel, Grid, Typography, Paper
} from "@mui/material";
import { MdLocationOn, MdEmail, MdPhone } from "react-icons/md";
import Hearder from "../component/common/header";
import styles from "../assets/scss/pagesScss/contact.module.scss";

const ContactUs = () => {
    const [loading, setLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            name: "",
            email: "",
            phone: "",
            subject: "",
            message: "",
            agree: false,
        },
        validationSchema: Yup.object({
            name: Yup.string().min(3, "Must be at least 3 characters").required("Name is required"),
            email: Yup.string().email("Invalid email address").required("Email is required"),
            phone: Yup.string().matches(/^[0-9]{10}$/, "Phone must be 10 digits").required("Phone number is required"),
            subject: Yup.string().required("Please select a subject"),
            message: Yup.string().min(10, "Message must be at least 10 characters").required("Message is required"),
            agree: Yup.boolean().oneOf([true], "You must agree to data collection"),
        }),
        onSubmit: async (values, { resetForm }) => {
            setLoading(true);

            const templateParams = {
                name: values.name,
                email: values.email,
                phone: values.phone,
                subject: values.subject,
                message: values.message,
            };

            try {
                // Send email using EmailJS
                await emailjs.send(
                    "service_m8i537v",
                    "template_bg365ei",
                    templateParams,
                    "I9Ovn54bd32uJPFm4"
                );

                // Optional: Save to Firestore
                await addDoc(collection(db, "contacts"), { ...values, timestamp: new Date() });

                toast.success("Message sent successfully!", { position: "top-right" });
                resetForm();
            } catch (error) {
                console.error("Error sending message:", error);
                toast.error("Failed to send message. Try again!", { position: "top-right" });
            } finally {
                setLoading(false);
            }
        },
    });

    return (
        <>
            <Hearder title="Contact us" breadcrumb="Contact us" />
            <section className={styles.contactSection}>
                <div className="container">
                    <Paper elevation={0} className={styles.contactContainer}>
                        <Grid container spacing={5}>
                            {/* Left Side - Contact Information */}
                            <Grid item xs={12} md={6}>
                                <div className={styles.contact_details}>
                                    <h4>Have Questions?</h4>
                                    <h5>Get in Touch!</h5>
                                    <p>It all starts with a conversation, let's connect.</p>
                                    <h6 className={styles.info_item}>
                                        <MdLocationOn size={50} color="#ff6600" className={styles.icons} />
                                        120/43, Anugraha, GPK layout, Bangalore, Karnataka, India
                                    </h6>
                                    <h6 className={styles.info_item}>
                                        <MdEmail size={50} color="#ff6600" className={styles.icons} />
                                        sales@smelectrifier.com
                                    </h6>
                                    <h6 className={styles.info_item}>
                                        <MdPhone size={50} color="#ff6600" className={styles.icons} />
                                        +91-73377 99457 / +91-96632 00144
                                    </h6>
                                </div>
                            </Grid>

                            {/* Right Side - Contact Form */}
                            <Grid item xs={12} md={6}>
                                <form onSubmit={formik.handleSubmit}>
                                    <Grid container spacing={2}>
                                        {/* Name & Email */}
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Name"
                                                name="name"
                                                variant="standard"
                                                {...formik.getFieldProps("name")}
                                                error={formik.touched.name && Boolean(formik.errors.name)}
                                                helperText={formik.touched.name && formik.errors.name}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Email Address"
                                                name="email"
                                                variant="standard"
                                                {...formik.getFieldProps("email")}
                                                error={formik.touched.email && Boolean(formik.errors.email)}
                                                helperText={formik.touched.email && formik.errors.email}
                                            />
                                        </Grid>

                                        {/* Subject & Phone */}
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                select
                                                fullWidth
                                                label="Select Subject"
                                                name="subject"
                                                variant="standard"
                                                {...formik.getFieldProps("subject")}
                                                error={formik.touched.subject && Boolean(formik.errors.subject)}
                                                helperText={formik.touched.subject && formik.errors.subject}
                                            >
                                                <MenuItem value="">Select Subject</MenuItem>
                                                <MenuItem value="General Inquiry">General Inquiry</MenuItem>
                                                <MenuItem value="Support">Support</MenuItem>
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Phone"
                                                name="phone"
                                                variant="standard"
                                                {...formik.getFieldProps("phone")}
                                                error={formik.touched.phone && Boolean(formik.errors.phone)}
                                                helperText={formik.touched.phone && formik.errors.phone}
                                            />
                                        </Grid>

                                        {/* Message */}
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="How can we help you?"
                                                name="message"
                                                variant="standard"
                                                multiline
                                                rows={4}
                                                {...formik.getFieldProps("message")}
                                                error={formik.touched.message && Boolean(formik.errors.message)}
                                                helperText={formik.touched.message && formik.errors.message}
                                            />
                                        </Grid>

                                        {/* Agreement */}
                                        <Grid item xs={12}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        name="agree"
                                                        checked={formik.values.agree}
                                                        onChange={formik.handleChange}
                                                    />
                                                }
                                                label="I agree that my data is collected and stored"
                                            />
                                            {formik.touched.agree && formik.errors.agree && (
                                                <Typography variant="body2" color="error">
                                                    {formik.errors.agree}
                                                </Typography>
                                            )}
                                        </Grid>

                                        {/* Submit */}
                                        <Grid item xs={12}>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                fullWidth
                                                type="submit"
                                                disabled={!formik.isValid || formik.isSubmitting || loading}
                                            >
                                                {loading ? "Sending..." : "Get in Touch"}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </form>
                            </Grid>
                        </Grid>
                    </Paper>
                </div>
            </section>

            {/* Google Map */}
            <div className="map-container">
                <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3889.161173211771!2d77.63875777454543!3d12.897355816502767!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae158e1cc6d93d%3A0x199c0f1ff4e96216!2sAnugraha%20Apartment%20120%2F43!5e0!3m2!1sen!2sin!4v1742499886341!5m2!1sen!2sin"
                    width="100%"
                    height={300}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
            </div>

            <ToastContainer style={{ zIndex: 9999 }} />
        </>
    );
};

export default ContactUs;
